import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Download,
  Edit,
  Trash2,
  Eye,
  MoreHorizontal,
  ArrowLeft,
  ChevronRight,
  Home,
  DollarSign,
  Users,
  Clock,
  CheckCircle
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import { Breadcrumb } from '../../components/Common'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import '../../components/ConfirmationModal/ConfirmationModal.css'
import paymentTermsMasterService from '../../services/paymentTermsMasterService'
// Consolidated CSS is imported globally'

const PaymentTermsMaster = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalPaymentTerms: 0,
    activePaymentTerms: 0,
    inactivePaymentTerms: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      
      // Load payment terms and stats in parallel
      const [paymentTermsResponse, statsResponse] = await Promise.all([
        paymentTermsMasterService.getAllPaymentTerms(),
        paymentTermsMasterService.getPaymentTermsStats()
      ])
      
      if (paymentTermsResponse.success) {
        setData(paymentTermsResponse.data || [])
        
        // Show empty state toast only once
        if (paymentTermsResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No payment terms found. Create your first payment terms!')
        }
      } else {
        toast.error(paymentTermsResponse.message || 'Failed to load payment terms')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading payment terms master records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view payment terms')
      } else {
        toast.error('Failed to load payment terms master records')
      }
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      accessorKey: 'paymentTermsCode',
      header: 'Payment Terms Code',
      size: 150,
      cell: ({ getValue }) => (
        <div className="sac-code-cell">
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'order',
      header: 'Order',
      size: 80,
      cell: ({ getValue }) => (
        <div className="rate-cell">
          <span className="rate-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'description',
      header: 'Description',
      size: 200,
      cell: ({ getValue }) => (
        <div className="description-cell">
          <span className="description-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 100,
      cell: ({ getValue }) => (
        <div className="status-cell">
          <span className={`status-badge ${getValue().toLowerCase()}`}>
            {getValue()}
          </span>
        </div>
      ),
    },
  ]

  const handleBack = () => {
    navigate('/sales')
  }

  const handleAddNew = () => {
    navigate('/sales/payment-terms-master/form')
  }

  const handleExport = () => {
    // Handle export
    console.log('Export Payment Terms Master data')
  }

  const handleEdit = (paymentTermsData) => {
    navigate('/sales/payment-terms-master/edit', { state: { paymentTermsData } })
  }

  const handleView = (paymentTermsData) => {
    navigate('/sales/payment-terms-master/view', { state: { paymentTermsData } })
  }

  const handleDelete = (paymentTermsData) => {
    setDeleteItem(paymentTermsData)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting payment terms...')
      // Use _id instead of id
      const response = await paymentTermsMasterService.deletePaymentTerms(deleteItem._id)
      if (response.success) {
        toast.success('Payment terms deleted successfully!', { autoClose: 2000 })
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete payment terms')
      }
    } catch (error) {
      console.error('Error deleting payment terms:', error)
      if (error.response?.status === 404) {
        toast.error('Payment terms not found')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete this payment terms')
      } else {
        toast.error('Failed to delete payment terms. Please try again.')
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Sales',
      path: '/sales',
      onClick: () => navigate('/sales')
    },
    {
      label: 'Masters',
      path: '/sales?tab=masters',
      onClick: () => navigate('/sales?tab=masters')
    },
    {
      label: 'Payment Terms Master',
      path: '/sales/payment-terms-master',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Payment Terms Master</h1>
          <p>Manage payment terms master data</p>
        </div>
        <div className="page-actions">
          <button className="btn-primary" onClick={handleAddNew}>
            <Plus size={18} />
            Add New
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <Clock size={16} />
            </div>
            <div className="metric-trend positive">
              <span>+2</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalPaymentTerms}</div>
          <div className="metric-title">Total Payment Terms</div>
          <div className="metric-subtitle">All payment terms configurations</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Active</span>
            </div>
          </div>
          <div className="metric-value">{stats.activePaymentTerms}</div>
          <div className="metric-title">Active Terms</div>
          <div className="metric-subtitle">Currently in use</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Users size={16} />
            </div>
            <div className="metric-trend negative">
              <span>Inactive</span>
            </div>
          </div>
          <div className="metric-value">{stats.inactivePaymentTerms}</div>
          <div className="metric-title">Inactive Terms</div>
          <div className="metric-subtitle">Disabled or archived</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalPaymentTerms}</div>
          <div className="metric-title">All Terms</div>
          <div className="metric-subtitle">Total payment terms</div>
        </div>
      </div>

      {/* Data Table */}
      <DataTable
        data={data}
        columns={columns}
        loading={loading}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onExport={handleExport}
        noCard={true}
        className="payment-terms-master-table"
        pageSize={10}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Payment Terms"
        message={`Are you sure you want to delete "${deleteItem?.description}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default PaymentTermsMaster
